/**
 * 地理工具：球面距离 / 同城判定 / 地图中心决策
 *
 * 设计原则：选区后，所有地图（首页 / submit / 选点）默认展开当前区域中心；
 * 仅当用户当前定位坐标与 zone 中心同城（默认 50km）时才允许以定位为中心，
 * 避免「在郑州选了区、地图却跳到北京」这种跨域体验。
 */

const EARTH_R_KM = 6371;

/** Haversine 球面距离（km） */
export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) *
      Math.cos(toRad(b.lat)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R_KM * Math.asin(Math.sqrt(s));
}

/** 默认同城阈值（km）覆盖绝大多数地级市 */
export const SAME_CITY_THRESHOLD_KM = 50;

/**
 * 决定地图默认中心：
 *  - 用户定位坐标存在 且 与 zone 中心同城 → 用定位
 *  - 否则用 zone 中心
 *  - 都没有：返回 null（由调用方决定兜底）
 */
export function pickMapCenter(
  zoneCenter: { lat: number; lng: number } | null | undefined,
  userLocation: { lat: number; lng: number } | null | undefined,
  thresholdKm = SAME_CITY_THRESHOLD_KM,
): [number, number] | null {
  if (zoneCenter) {
    if (userLocation) {
      const d = haversineKm(zoneCenter, userLocation);
      if (d <= thresholdKm) return [userLocation.lng, userLocation.lat];
    }
    return [zoneCenter.lng, zoneCenter.lat];
  }
  if (userLocation) return [userLocation.lng, userLocation.lat];
  return null;
}
